/**
 * Render Graph 执行器（M3-09 / DESIGN.md §4.4「按 tile 分批执行」）。
 *
 * 按 {@link RenderGraph.topologicalOrder} 走一遍图，逐块产出最终纹理。
 * 覆盖范围见 state/DECISIONS.md 的 ADR-0005：raster/text 图层、混合模式、
 * 组语义（穿透/非穿透）、蒙版、剪贴。调整图层/图层样式/智能滤镜不在此列——
 * `planFromDocument`（应用层）遇到这些直接不产出计划，调用方回退 CPU。
 *
 * **折叠无关 draw**：fillOpacity 只是标量乘 alpha、Mask 只是纹理乘 alpha、
 * Clip 只是另一张纹理的 alpha 相交——三者都不需要单独的渲染目标，直接记在
 * "待应用的修饰符"里，到下一个 Blend 节点才真正提交一次 draw、一次性套用
 * 全部修饰符。一层没有蒙版没有剪贴时，从 SourceTile 到 Blend 之间零中间
 * draw——这与图的名义 `drawsPerTile`（每个非 SourceTile 节点算一次）不同，
 * 后者是保守估计，只影响 {@link scheduleTiles} 的分批粒度，不影响正确性。
 */
import type { GpuBackend, GpuTexture } from '../backend';
import type { RenderGraph, NodeId, RenderNode } from '../graph';
import { compositeShaderSource } from '../shaders/composite';
import { blendModeIndex, type AnyBlendMode } from '../shaders/blend/generate';
import type { GpuTexturePool } from './texture-pool';
import type { TileSource } from './tile-source';

/** 一个节点在某块上的求值结果：一张已有像素的纹理 + 尚未套用的 alpha 修饰符。 */
interface PixelBundle {
  readonly color: GpuTexture;
  /** 额外的标量 alpha 乘数（fillOpacity）。 */
  readonly scale: number;
  /** 额外的纹理 alpha 调制（Mask 的 .r 通道）。null = 无。 */
  readonly mask: GpuTexture | null;
  /** 额外的纹理 alpha 相交（Clip 基底的 .a 通道）。null = 无。 */
  readonly clip: GpuTexture | null;
}

function isBaked(bundle: PixelBundle): boolean {
  return bundle.scale === 1 && bundle.mask === null && bundle.clip === null;
}

export interface ExecuteOptions {
  readonly backend: GpuBackend;
  readonly graph: RenderGraph;
  readonly source: TileSource;
  readonly pool: GpuTexturePool;
  /** 本帧要渲染的块（文档块坐标）。 */
  readonly tiles: readonly { readonly tx: number; readonly ty: number }[];
  readonly tileSize: number;
  /** 输出纹理，尺寸 = 视口像素尺寸。 */
  readonly target: GpuTexture;
  /** target 左上角对应的文档像素坐标。 */
  readonly originX: number;
  readonly originY: number;
  readonly signal?: AbortSignal;
}

export interface ExecuteStats {
  readonly tiles: number;
  readonly draws: number;
}

/** 合成 program 只需建一次；跨帧、跨块复用。 */
let cachedProgramBackend: GpuBackend | null = null;
let cachedProgram: ReturnType<GpuBackend['createProgram']> | null = null;

function compositeProgram(backend: GpuBackend): ReturnType<GpuBackend['createProgram']> {
  if (cachedProgramBackend !== backend || cachedProgram === null) {
    cachedProgram = backend.createProgram({
      source: compositeShaderSource(),
      targetFormat: 'rgba8unorm',
    });
    cachedProgramBackend = backend;
  }
  return cachedProgram;
}

/** 供上下文丢失恢复后调用：旧 backend 的 program 已失效，强制下次重建。 */
export function resetCompositeProgramCache(): void {
  cachedProgramBackend = null;
  cachedProgram = null;
}

export function executeGraph(options: ExecuteOptions): ExecuteStats {
  const { backend, graph, source, pool, tileSize, target, originX, originY } = options;
  const order = graph.topologicalOrder();
  const program = compositeProgram(backend);
  let draws = 0;

  // 第一遍：每块独立走图，产出「这块的最终纹理」，中间纹理用完即还给池
  const finals: Array<{ tx: number; ty: number; texture: GpuTexture; owned: boolean }> = [];
  for (const { tx, ty } of options.tiles) {
    if (options.signal?.aborted === true) throw new DOMException('操作已中止', 'AbortError');
    const result = renderTile(backend, program, order, source, pool, tx, ty, tileSize);
    draws += result.draws;
    finals.push({ tx, ty, texture: result.texture, owned: result.owned });
  }

  // 第二遍：一次 drawBatch 把所有块摆到视口输出纹理的正确位置——
  // 每块可能来自不同纹理/池对象，这正是 BatchItem 逐项独立 uniforms 的用途
  if (finals.length > 0) {
    const items = finals
      .map(({ tx, ty, texture }) => {
        const x = tx * tileSize - originX;
        const y = ty * tileSize - originY;
        // WebGPU 的 setViewport 要求矩形整个落在 target 内（越界是校验错误）——
        // 文档尺寸不是 tileSize 整数倍时，边缘块的名义视口天然会伸出 target
        // 边界（比如 300×200 的文档配 256 的 tileSize，两块的名义视口高度都
        // 是 256 > 200）。Chrome 的 WebGPU 实现对此容忍，WebKit 会真的把
        // command encoder 锁死、这一批 draw 全部作废——图片在 Safari 上开出来
        // 是纯灰占位，根源就在这，而不是罕见的边界情况：文档尺寸对不上
        // tileSize 整数倍是常态。视口矩形必须先夹到 target 范围内；
        // 夹的只是"画多少"，不影响"画在哪"——offset uniform 仍然用未夹的
        // x/y，它换算的是「target 坐标 → tile 内局部坐标」，跟视口矩形本身
        // 裁没裁边无关，已经画的那部分像素位置依然要对得上原来的偏移量。
        const vx = Math.max(0, x);
        const vy = Math.max(0, y);
        const vw = Math.min(x + tileSize, target.width) - vx;
        const vh = Math.min(y + tileSize, target.height) - vy;
        if (vw <= 0 || vh <= 0) return null; // 名义视口整个落在 target 外，没有可见部分
        return {
          program: blitProgram(backend),
          vertexCount: 3,
          // 片元的 @builtin(position)/gl_FragCoord 是**目标纹理整体**的坐标，
          // 不是这个 viewport 子矩形的局部坐标——item 的 viewport 偏移量必须
          // 减掉，否则任何偏移量非零的块都会用越界坐标去采样自己的 256×256
          // 纹理（越界 textureLoad 恒返回 0，读到的是一片透明黑，M3-09 的
          // 首个多块场景就是这么发现的）。
          //
          // u_offsetY 的取值两个后端不一样：WebGPU 的 position builtin 与
          // 调用方传入的 viewport 同为左上角原点，直接用 y 就对；WebGL2
          // 的 gl_FragCoord 是原生左下角原点，而后端把调用方的左上角 y
          // 换算成了 `target.height − y − h` 才真正传给 gl.viewport——
          // 片元看到的因此是那个换算后的值，offset 必须用同一个值，
          // 否则纹理高度与 tile 高度不同（本场景 200 < 256）时就会算错。
          uniforms: {
            u_source: texture,
            u_offsetX: x,
            u_offsetY: backend.kind === 'webgl2' ? target.height - y - tileSize : y,
          },
          viewport: [vx, vy, vw, vh] as const,
        };
      })
      .filter((item) => item !== null);
    backend.drawBatch({ target, clear: [0, 0, 0, 0], items });
    draws += finals.length;
    // 只归还真的从池子借出来的纹理——canvasTexture()/emptyTexture() 这类
    // 共享单例（一块 tile 里所有可见图层都被跳过时会直接原样冒出来）绝不能
    // 进 release()：池子不检查纹理是不是自己发出去的，一旦被当成"空闲纹理"
    // 塞回去，下次 acquire() 就可能把这张全局共享的画布底色纹理批给别的用途
    // 随便画——图层再显示回来时用到的画布底色可能已经被污染成任意内容。
    for (const { texture, owned } of finals) {
      if (owned) pool.release(texture);
    }
  }

  return { tiles: options.tiles.length, draws };
}

interface TileResult {
  readonly draws: number;
  readonly texture: GpuTexture;
  /**
   * `texture` 是不是从 `pool.acquire()` 借来的。
   *
   * 一块 tile 里所有可见图层都被跳过（比如唯一的图层刚被隐藏）时，
   * `materialize()` 的 `isBaked` 短路会直接把 `source.canvasTexture()`/
   * `emptyTexture()` 这类**共享单例纹理**原样当作这块的最终结果返回——
   * 那不是从池子借出来的，调用方绝不能把它交还给 `pool.release()`。
   * 池子的 `release()` 不检查纹理是不是自己发出去的，一旦被塞进空闲桶，
   * 下次任何 `acquire()` 都可能把这张全局共享的画布底色纹理当成"一块
   * 空白刻度纸"批出去随便画——图层再显示回来时用到的可能就是这张已经
   * 被别的东西画脏了的"画布"，看起来像是"显示没恢复"，其实是共享纹理
   * 被污染了。
   */
  readonly owned: boolean;
}

function renderTile(
  backend: GpuBackend,
  program: ReturnType<GpuBackend['createProgram']>,
  order: readonly RenderNode[],
  source: TileSource,
  pool: GpuTexturePool,
  tx: number,
  ty: number,
  tileSize: number,
): TileResult {
  const results = new Map<NodeId, PixelBundle>();
  const acquired: GpuTexture[] = [];
  let draws = 0;
  let outputTexture: GpuTexture | null = null;

  const tileTexture = (layer: string): GpuTexture => {
    if (layer === ' canvas') return source.canvasTexture();
    if (layer === ' empty') return source.emptyTexture();
    return source.tileFor(layer, tx, ty) ?? source.emptyTexture();
  };

  /** 把一个 bundle 的修饰符全部烘进一张干净纹理（cb=透明，一次 Blend 形状的 draw）。 */
  const materialize = (bundle: PixelBundle): GpuTexture => {
    if (isBaked(bundle)) return bundle.color;
    const baked = pool.acquire(tileSize, tileSize, 'materialize');
    acquired.push(baked);
    backend.draw({
      program,
      target: baked,
      vertexCount: 3,
      clear: [0, 0, 0, 0],
      uniforms: {
        u_backdrop: source.emptyTexture(),
        u_source: bundle.color,
        u_mask: bundle.mask ?? source.emptyTexture(),
        u_clip: bundle.clip ?? source.emptyTexture(),
        u_mode: 0, // normal：cb 透明时 blended 恒等于 cs，等价于纯 alpha 调制
        u_opacity: bundle.scale,
        u_maskEnabled: bundle.mask !== null ? 1 : 0,
        u_clipEnabled: bundle.clip !== null ? 1 : 0,
        u_linear: 0,
      },
    });
    draws++;
    return baked;
  };

  for (const node of order) {
    if (node.kind === 'SourceTile') {
      results.set(node.id, { color: tileTexture(node.layer), scale: 1, mask: null, clip: null });
      continue;
    }
    if (node.kind === 'Adjustment') {
      const input = results.get(node.inputs[0])!;
      if (node.adjustment === 'fillOpacity') {
        const amount = node.params['amount'] ?? 1;
        results.set(node.id, { ...input, scale: input.scale * amount });
      } else {
        // blendIf 等尚未支持：按恒等处理（不是正确实现，只是不让执行器炸掉；
        // planFromDocument 目前不会产出需要真正 blendIf 效果的计划）
        results.set(node.id, input);
      }
      continue;
    }
    if (node.kind === 'Effect') {
      // 图层样式暂不支持（ADR-0005）；恒等直通
      results.set(node.id, results.get(node.inputs[0])!);
      continue;
    }
    if (node.kind === 'Mask') {
      const content = results.get(node.inputs[0])!;
      const maskSrc = results.get(node.inputs[1])!;
      results.set(node.id, { ...content, mask: maskSrc.color });
      continue;
    }
    if (node.kind === 'Clip') {
      const content = results.get(node.inputs[0])!;
      const base = results.get(node.inputs[1])!;
      results.set(node.id, { ...content, clip: materialize(base) });
      continue;
    }
    if (node.kind === 'Blend') {
      const backdrop = results.get(node.inputs[0])!;
      const source_ = results.get(node.inputs[1])!;
      const out = pool.acquire(tileSize, tileSize, 'blend');
      acquired.push(out);
      backend.draw({
        program,
        target: out,
        vertexCount: 3,
        uniforms: {
          u_backdrop: materialize(backdrop),
          u_source: source_.color,
          u_mask: source_.mask ?? source.emptyTexture(),
          u_clip: source_.clip ?? source.emptyTexture(),
          u_mode: blendModeIndex(node.blendMode as AnyBlendMode),
          u_opacity: source_.scale * node.opacity,
          u_maskEnabled: source_.mask !== null ? 1 : 0,
          u_clipEnabled: source_.clip !== null ? 1 : 0,
          u_linear: node.linearSpace ? 1 : 0,
        },
      });
      draws++;
      results.set(node.id, { color: out, scale: 1, mask: null, clip: null });
      continue;
    }
    if (node.kind === 'Output') {
      outputTexture = materialize(results.get(node.inputs[0])!);
      continue;
    }
    if (node.kind === 'Transform') {
      // 编译器从不产出 Transform 节点（group.ts 里没有任何地方 add 它）
      throw new Error('Transform 节点尚未实现（compileCompositePlan 目前也不产出它）。');
    }
  }

  if (outputTexture === null) throw new Error('图里没有 Output 节点——compileCompositePlan 的不变量被破坏了。');
  // 中间纹理全部还给池，只留 outputTexture 活到调用方把它 blit 进视口输出
  const owned = acquired.includes(outputTexture);
  for (const texture of acquired) {
    if (texture !== outputTexture) pool.release(texture);
  }
  return { draws, texture: outputTexture, owned };
}

let cachedBlit: { backend: GpuBackend; program: ReturnType<GpuBackend['createProgram']> } | null =
  null;

/** 直通 blit：把一张纹理原样画到 target。用于把某块的最终结果放进视口输出纹理。 */
function blitProgram(backend: GpuBackend): ReturnType<GpuBackend['createProgram']> {
  if (cachedBlit === null || cachedBlit.backend !== backend) {
    cachedBlit = {
      backend,
      program: backend.createProgram({
        source: {
          // u_offsetX/u_offsetY：这个 item 的 viewport 左上角在目标纹理里的
          // 像素坐标（与传给 BatchItem.viewport 的 x,y 同一个值）。片元的
          // @builtin(position)/gl_FragCoord 报的是**目标纹理整体**坐标，
          // 减掉这个偏移才是纹理自己的局部坐标（0..tileSize）。
          wgsl: `
struct Params { u_offsetX: f32, u_offsetY: f32 };
@group(0) @binding(0) var<uniform> params: Params;
@group(0) @binding(1) var u_source: texture_2d<f32>;

@vertex
fn vs_main(@builtin(vertex_index) idx: u32) -> @builtin(position) vec4f {
  var pos = array<vec2f, 3>(vec2f(-1.0, -1.0), vec2f(3.0, -1.0), vec2f(-1.0, 3.0));
  return vec4f(pos[idx], 0.0, 1.0);
}
@fragment
fn fs_main(@builtin(position) pos: vec4f) -> @location(0) vec4f {
  let local = vec2<i32>(pos.xy) - vec2<i32>(i32(params.u_offsetX), i32(params.u_offsetY));
  return textureLoad(u_source, local, 0);
}
`,
          glsl: {
            vertex: `#version 300 es
void main() {
  vec2 pos[3] = vec2[3](vec2(-1.0, -1.0), vec2(3.0, -1.0), vec2(-1.0, 3.0));
  gl_Position = vec4(pos[gl_VertexID], 0.0, 1.0);
}
`,
            fragment: `#version 300 es
precision highp float;
uniform float u_offsetX;
uniform float u_offsetY;
uniform sampler2D u_source;
out vec4 fragColor;
void main() {
  ivec2 local = ivec2(gl_FragCoord.xy) - ivec2(int(u_offsetX), int(u_offsetY));
  fragColor = texelFetch(u_source, local, 0);
}
`,
          },
        },
        targetFormat: 'rgba8unorm',
      }),
    };
  }
  return cachedBlit.program;
}
